<script>
let topWindowZIndex = 3000
</script>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useQuasar } from 'quasar'

import { useDetectDevice } from '@/composables/device'

const PANEL_MIN_WIDTH_REM = 20
const PANEL_MAX_SCREEN_RATIO = 0.7
const WINDOW_MIN_WIDTH_REM = 20
const WINDOW_MIN_HEIGHT_REM = 24
const SHEET_SNAP_POINTS = Object.freeze([0.5, 0.94])
const SHEET_CLOSE_THRESHOLD = 0.15

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  docked: {
    type: Boolean,
    default: true,
  },
  width: {
    type: Number,
    required: true,
  },
  bounds: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue', 'update:width', 'update:bounds'])

const $q = useQuasar()
const { isMobile } = useDetectDevice()

const windowBounds = ref({ ...props.bounds })
const windowZIndex = ref(++topWindowZIndex)
const sheetFraction = ref(SHEET_SNAP_POINTS[0])
const drawerShown = ref(props.modelValue)

const rootFontSize = () => parseFloat(getComputedStyle(document.documentElement).fontSize)
const screenRem = () => ({ width: $q.screen.width / rootFontSize(), height: $q.screen.height / rootFontSize() })
const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

const sheetStyle = computed(() => ({ height: `${sheetFraction.value * 100}dvh` }))
const drawerWidth = computed(() => Math.round(props.width * rootFontSize()))
const windowStyle = computed(() => {
  const { left, top, width, height } = windowBounds.value
  return { left: `${left}rem`, top: `${top}rem`, width: `${width}rem`, height: `${height}rem`, zIndex: windowZIndex.value }
})

const bringToFront = () => {
  if (windowZIndex.value !== topWindowZIndex) windowZIndex.value = ++topWindowZIndex
}

watch(() => props.modelValue, async(show) => {
  if (!show) {
    drawerShown.value = false
    return
  }
  bringToFront()
  // Every right drawer writes its offset on the shared layout side: open after the other panel's close has landed.
  await nextTick()
  drawerShown.value = props.modelValue
})

const close = () => emit('update:modelValue', false)

const onDrawerResize = ({ delta }) => {
  const maxWidth = screenRem().width * PANEL_MAX_SCREEN_RATIO
  emit('update:width', clamp(props.width - delta.x / rootFontSize(), PANEL_MIN_WIDTH_REM, maxWidth))
}

const onSheetPan = ({ delta, isFinal }) => {
  sheetFraction.value = clamp(sheetFraction.value - delta.y / $q.screen.height, 0, SHEET_SNAP_POINTS.at(-1))
  if (!isFinal) return
  if (sheetFraction.value < SHEET_SNAP_POINTS[0] - SHEET_CLOSE_THRESHOLD) {
    close()
    sheetFraction.value = SHEET_SNAP_POINTS[0]
    return
  }
  sheetFraction.value = SHEET_SNAP_POINTS.reduce((nearest, point) => (Math.abs(point - sheetFraction.value) < Math.abs(nearest - sheetFraction.value) ? point : nearest))
}

const updateWindowBounds = (bounds, isFinal) => {
  windowBounds.value = bounds
  if (isFinal) emit('update:bounds', { ...bounds })
}

const move = ({ delta, isFinal }) => {
  const { left, top, width, height } = windowBounds.value
  const screen = screenRem()
  updateWindowBounds({
    width,
    height,
    left: clamp(left + delta.x / rootFontSize(), 0, screen.width - width),
    top: clamp(top + delta.y / rootFontSize(), 0, screen.height - height),
  }, isFinal)
}

const onWindowResize = ({ delta, isFinal }) => {
  const { left, top, width, height } = windowBounds.value
  const screen = screenRem()
  updateWindowBounds({
    left,
    top,
    width: clamp(width + delta.x / rootFontSize(), WINDOW_MIN_WIDTH_REM, screen.width - left),
    height: clamp(height + delta.y / rootFontSize(), WINDOW_MIN_HEIGHT_REM, screen.height - top),
  }, isFinal)
}
</script>

<template>
  <transition
    v-if="isMobile"
    appear
    enter-active-class="animate__animated animate__slideInUp"
    leave-active-class="animate__animated animate__slideOutDown"
  >
    <q-card v-if="modelValue" class="dockable-sheet column no-wrap" :style="sheetStyle">
      <div v-touch-pan.vertical.prevent.mouse="onSheetPan" class="dockable-sheet__handle">
        <span class="dockable-sheet__grip" />
      </div>
      <div class="col dockable-sheet__body">
        <slot :floating="false" :move="move" :close="close" />
      </div>
    </q-card>
  </transition>

  <q-drawer
    v-else-if="docked"
    elevated
    :model-value="drawerShown"
    side="right"
    bordered
    :width="drawerWidth"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="full-height relative-position">
      <div v-touch-pan.horizontal.prevent.mouse="onDrawerResize" class="dockable-drawer__resize-handle" />
      <slot :floating="false" :move="move" :close="close" />
    </div>
  </q-drawer>

  <transition
    v-else
    appear
    enter-active-class="animate__animated animate__fadeIn"
    leave-active-class="animate__animated animate__fadeOut"
  >
    <q-card
      v-if="modelValue"
      bordered
      class="dockable-window"
      :style="windowStyle"
      @pointerdown.capture="bringToFront"
    >
      <slot :floating="true" :move="move" :close="close" />
      <div v-touch-pan.prevent.mouse="onWindowResize" class="dockable-window__resize-handle">
        <q-icon name="mdi-resize-bottom-right" size="xs" color="grey-6" />
      </div>
    </q-card>
  </transition>
</template>

<style scoped lang="scss">
.dockable-drawer__resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: .375rem;
  cursor: ew-resize;
  z-index: 1;
}

.dockable-sheet {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3000;
  overflow: hidden;
  border-radius: $app-border-radius $app-border-radius 0 0;
  box-shadow: 0 -.25rem 1.5rem rgba(0, 0, 0, 0.18);
}

.dockable-sheet__handle {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: .5rem 0 .25rem;
  cursor: grab;
  touch-action: none;
}

.dockable-sheet__body {
  min-height: 0;

  :deep(.custom-fixed-top) {
    box-shadow: none !important;
  }
}

.dockable-sheet__grip {
  width: 2.5rem;
  height: .3125rem;
  border-radius: .25rem;
  background: $grey-4;
}

.dockable-window {
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: .75rem;
  box-shadow: 0 .5rem 2rem rgba(0, 0, 0, 0.18);
}

.dockable-window__resize-handle {
  position: absolute;
  right: .125rem;
  bottom: .125rem;
  cursor: nwse-resize;
  line-height: 0;
}
</style>
